// Helpers for the Leistungstest (Performance Test) feature.

export const DIMENSIONS = [
  { key: "technik", label: "Technik", description: "Ballkontrolle, Pass, Schuss" },
  { key: "schnelligkeit", label: "Schnelligkeit", description: "Sprint & Antritt" },
  { key: "ausdauer", label: "Ausdauer", description: "Kondition & Laufleistung" },
  { key: "taktik", label: "Taktik", description: "Reaktion & Entscheidung" },
];

export const UNITS = [
  { key: "count", label: "Anzahl / Punkte", short: "Pkt." },
  { key: "seconds", label: "Zeit (Sekunden)", short: "s" },
  { key: "level", label: "Level / Intervall", short: "Lvl" },
];

export const DIRECTIONS = [
  { key: "higher", label: "Höher ist besser", short: "↑" },
  { key: "lower", label: "Niedriger ist besser (z. B. Zeit)", short: "↓" },
];

export function dimensionLabel(key) {
  return DIMENSIONS.find((d) => d.key === key)?.label || key;
}

export function unitShort(key) {
  return UNITS.find((u) => u.key === key)?.short || "";
}

export function unitLabel(key) {
  return UNITS.find((u) => u.key === key)?.label || key;
}

// Format an attempt value depending on unit type
export function formatValue(value, unit) {
  if (value == null) return "—";
  if (unit === "seconds") return value.toFixed(2) + " s";
  if (unit === "level") return "Lvl " + Math.round(value);
  return String(Math.round(value));
}

// Pick the best (valid) attempt of a player according to direction.
// Returns null if no valid attempt.
export function bestAttempt(attempts, direction) {
  const valids = (attempts || []).filter((a) => !a.fehlversuch && a.value != null && !Number.isNaN(a.value));
  if (valids.length === 0) return null;
  if (direction === "lower") {
    return valids.reduce((acc, a) => (acc == null || a.value < acc.value ? a : acc), null);
  }
  return valids.reduce((acc, a) => (acc == null || a.value > acc.value ? a : acc), null);
}

// Average of best attempts across all players who have a valid result.
// Returns { avg, bestPlayer (id), bestValue, count, ranking }
export function aggregateResults(test, players) {
  const items = [];
  for (const p of players) {
    const r = (test.results || {})[p.id];
    if (!r) continue;
    const ba = bestAttempt(r.attempts, test.direction);
    if (!ba) continue;
    items.push({ player: p, bestValue: ba.value, all: r.attempts, groesse_cm: r.groesse_cm, gewicht_kg: r.gewicht_kg });
  }
  if (items.length === 0) return { avg: null, top: null, count: 0, ranking: [] };

  const sorted = [...items].sort((a, b) =>
    test.direction === "lower" ? a.bestValue - b.bestValue : b.bestValue - a.bestValue
  );
  const avg = items.reduce((s, i) => s + i.bestValue, 0) / items.length;
  return {
    avg: Math.round(avg * 100) / 100,
    top: sorted[0],
    count: items.length,
    ranking: sorted,
  };
}

// History of a player across multiple performance-tests of the same test_def_id (or by name).
// Returns chronological list of { datum, bestValue, deltaPct }
export function playerHistoryForDef(playerId, tests, testKey) {
  // testKey can be test_def_id (preferred). Pre-sort by datum ascending.
  const matched = tests
    .filter((t) => t.test_def_id === testKey)
    .sort((a, b) => a.datum.localeCompare(b.datum));

  const series = [];
  for (const t of matched) {
    const r = (t.results || {})[playerId];
    if (!r) continue;
    const ba = bestAttempt(r.attempts, t.direction);
    if (!ba) continue;
    series.push({ datum: t.datum, bestValue: ba.value, unit: t.unit, direction: t.direction });
  }
  // Add deltaPct vs previous entry
  for (let i = 1; i < series.length; i++) {
    const prev = series[i - 1].bestValue;
    const cur = series[i].bestValue;
    if (prev !== 0) {
      let pct = ((cur - prev) / prev) * 100;
      // For "lower" tests: improvement is negative delta → invert sign so improvement is positive %
      if (series[i].direction === "lower") pct = -pct;
      series[i].deltaPct = Math.round(pct * 10) / 10;
    }
  }
  return series;
}
